import React, { useMemo, useState, useRef, useEffect } from "react";
import { TextField, IconButton, InputAdornment, Popover, Box, Typography, Grid } from "@mui/material";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import {
  format as fnsFormat,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  isSameDay,
  isSameMonth,
  isToday,
  setMonth,
  setYear,
  parse as fnsParse,
  isValid,
} from "date-fns";
import { ptBR, es, enUS, ar, tr } from "date-fns/locale";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

const LOCALES = { pt: ptBR, es, en: enUS, ar, tr };
const DEFAULT_DISPLAY_FORMAT = "dd/MM/yyyy";

const getLocale = () => {
  const lng = (i18n.language || "pt").substring(0, 2);
  return LOCALES[lng] || ptBR;
};

// Labels da semana (D-S-T-Q-Q-S-S em PT, M-T-W-T-F-S-S em EN, etc.) — vindos do próprio locale
const getWeekLabels = (locale) => {
  const base = new Date(2024, 0, 7); // domingo
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return fnsFormat(d, "EEEEE", { locale });
  });
};

/**
 * SimpleDatePicker - DatePicker padrão do sistema.
 *
 * Substitui @mui/x-date-pickers (que tinha problemas de scroll em v5.0.8).
 *
 * Props:
 *  - value (Date | null): data selecionada
 *  - onChange (Date => void): handler de seleção
 *  - label (string): label do TextField
 *  - displayFormat (string): formato de exibição (default "dd/MM/yyyy")
 *  - minDate / maxDate (Date): limites
 *  - disabled (boolean)
 *  - error (boolean)
 *  - helperText (string)
 *  - size ("small" | "medium")
 *  - fullWidth (boolean)
 *  - placeholder (string)
 *  - sx (object): sx extra para o TextField
 *  - TextFieldProps (object): props extras pro TextField interno
 *  - PopoverProps (object): props extras pro Popover
 */
const SimpleDatePicker = ({
  value,
  onChange,
  label,
  displayFormat = DEFAULT_DISPLAY_FORMAT,
  minDate,
  maxDate,
  disabled = false,
  error = false,
  helperText,
  size = "small",
  fullWidth = true,
  placeholder,
  sx,
  TextFieldProps = {},
  PopoverProps = {},
}) => {
  const themeV4 = useThemeV4();
  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewDate, setViewDate] = useState(value || new Date());
  const [view, setView] = useState("days");
  const [textValue, setTextValue] = useState(value ? fnsFormat(value, displayFormat) : "");

  const locale = getLocale();
  const WEEK_LABELS = useMemo(() => getWeekLabels(locale), [locale]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    setTextValue(value ? fnsFormat(value, displayFormat) : "");
  }, [value, displayFormat]);

  const handleOpen = () => {
    if (disabled) return;
    setViewDate(value || new Date());
    setView("days");
    setAnchorEl(inputRef.current);
  };
  const handleClose = () => setAnchorEl(null);

  const isInRange = (d) => {
    if (minDate && d < minDate) return false;
    if (maxDate && d > maxDate) return false;
    return true;
  };

  const handleSelectDay = (day) => {
    if (!isInRange(day)) return;
    onChange?.(day);
    handleClose();
  };

  const handleTextChange = (e) => {
    const v = e.target.value;
    setTextValue(v);
    if (!v) {
      onChange?.(null);
      return;
    }
    const parsed = fnsParse(v, displayFormat, new Date());
    if (isValid(parsed) && isInRange(parsed)) {
      onChange?.(parsed);
      setViewDate(parsed);
    }
  };

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 0 });
    const arr = [];
    let cur = start;
    while (cur <= end) {
      arr.push(cur);
      cur = addDays(cur, 1);
    }
    return arr;
  }, [viewDate]);

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const min = minDate ? minDate.getFullYear() : current - 50;
    const max = maxDate ? maxDate.getFullYear() : current + 50;
    const arr = [];
    for (let y = min; y <= max; y++) arr.push(y);
    return arr;
  }, [minDate, maxDate]);

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => fnsFormat(setMonth(new Date(2020, 0, 1), i), "LLL", { locale })),
    [locale]
  );

  const monthLabel = fnsFormat(viewDate, "MMMM yyyy", { locale });

  return (
    <>
      <TextField
        inputRef={inputRef}
        label={label}
        value={textValue}
        onChange={handleTextChange}
        onClick={handleOpen}
        disabled={disabled}
        error={error}
        helperText={helperText}
        placeholder={placeholder || displayFormat.toLowerCase()}
        fullWidth={fullWidth}
        size={size}
        variant="outlined"
        sx={sx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleOpen} edge="end" disabled={disabled}>
                <CalendarDays size={18} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        {...TextFieldProps}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "12px",
            backgroundColor: isDark ? "#11141b" : "#ffffff",
            backgroundImage: "none",
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
            boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.7)" : "0 12px 40px rgba(15,23,42,0.15)",
            p: 1.5,
            width: 300,
          },
        }}
        {...PopoverProps}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box
            onClick={() => setView(view === "years" ? "days" : "years")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
              "&:hover": { background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14,
                textTransform: "capitalize",
                color: isDark ? "#e9edef" : "#0f172a",
              }}
            >
              {monthLabel}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setViewDate((d) => addMonths(d, -1))}
              sx={{
                borderRadius: "10px",
                color: isDark ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.65)",
                "&:hover": { background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
              }}
            >
              <ChevronLeft size={18} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewDate((d) => addMonths(d, 1))}
              sx={{
                borderRadius: "10px",
                color: isDark ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.65)",
                "&:hover": { background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
              }}
            >
              <ChevronRight size={18} />
            </IconButton>
          </Box>
        </Box>

        {view === "days" && (
          <>
            <Grid container columns={7} sx={{ mb: 0.5 }}>
              {WEEK_LABELS.map((w, i) => (
                <Grid
                  item
                  xs={1}
                  key={i}
                  sx={{
                    textAlign: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: isDark ? "rgba(255,255,255,0.5)" : "rgba(15,23,42,0.5)",
                    py: 0.5,
                  }}
                >
                  {w}
                </Grid>
              ))}
            </Grid>
            <Grid container columns={7}>
              {days.map((d, i) => {
                const inMonth = isSameMonth(d, viewDate);
                const selected = value && isSameDay(d, value);
                const today = isToday(d);
                const enabled = isInRange(d);
                return (
                  <Grid item xs={1} key={i} sx={{ display: "flex", justifyContent: "center", py: 0.25 }}>
                    <Box
                      onClick={() => enabled && handleSelectDay(d)}
                      sx={{
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "10px",
                        cursor: enabled ? "pointer" : "not-allowed",
                        opacity: enabled ? 1 : 0.3,
                        fontSize: 13,
                        fontWeight: selected || today ? 700 : 500,
                        color: selected
                          ? PRIMARY_CONTRAST
                          : !inMonth
                          ? isDark
                            ? "rgba(255,255,255,0.25)"
                            : "rgba(15,23,42,0.3)"
                          : today
                          ? PRIMARY_MAIN
                          : isDark
                          ? "#e9edef"
                          : "#0f172a",
                        background: selected
                          ? `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`
                          : "transparent",
                        border: today && !selected ? `1px solid ${PRIMARY_MAIN}` : "1px solid transparent",
                        boxShadow: selected ? `0 4px 12px ${PRIMARY_MAIN}55` : "none",
                        transition: "background .15s, color .15s",
                        "&:hover": selected || !enabled
                          ? {}
                          : { background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
                      }}
                    >
                      {fnsFormat(d, "d")}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {view === "years" && (
          <Box
            sx={{
              maxHeight: 240,
              overflowY: "auto",
              mt: 0.5,
              ...(themeV4?.scrollbarStyles || {}),
            }}
          >
            <Grid container columns={4} spacing={0.5}>
              {years.map((y) => {
                const selected = y === viewDate.getFullYear();
                return (
                  <Grid item xs={1} key={y}>
                    <Box
                      ref={
                        selected
                          ? (el) => {
                              if (el && view === "years") {
                                el.scrollIntoView({ block: "center", behavior: "instant" });
                              }
                            }
                          : undefined
                      }
                      onClick={() => {
                        setViewDate((d) => setYear(d, y));
                        setView("months");
                      }}
                      sx={{
                        textAlign: "center",
                        py: 1,
                        borderRadius: "10px",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: selected ? 700 : 500,
                        color: selected ? PRIMARY_CONTRAST : isDark ? "#e9edef" : "#0f172a",
                        background: selected
                          ? `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`
                          : "transparent",
                        "&:hover": selected
                          ? {}
                          : { background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
                      }}
                    >
                      {y}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {view === "months" && (
          <Grid container columns={3} spacing={0.5} sx={{ mt: 0.5 }}>
            {months.map((m, i) => {
              const selected = i === viewDate.getMonth();
              return (
                <Grid item xs={1} key={m}>
                  <Box
                    onClick={() => {
                      setViewDate((d) => setMonth(d, i));
                      setView("days");
                    }}
                    sx={{
                      textAlign: "center",
                      py: 1.25,
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: selected ? 700 : 500,
                      textTransform: "capitalize",
                      color: selected ? PRIMARY_CONTRAST : isDark ? "#e9edef" : "#0f172a",
                      background: selected
                        ? `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`
                        : "transparent",
                      "&:hover": selected
                        ? {}
                        : { background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
                    }}
                  >
                    {m.replace(".", "")}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Popover>
    </>
  );
};

export default SimpleDatePicker;
